<template>
    <div v-if="isRendered">
        <div class="row mb-5">
            <div class="col-12">
                <div>
                    <p class="h5 mb-2">
                        <span class="icon icon-100 icon-glyphicons-basic-stopwatch mr-2"></span>
                        <span>Timer: {{ timer.label }}</span>
                    </p>
                    <span class="badge badge-gray">{{ learnerCount }} Unique Learners</span>
                </div>
            </div>
        </div>
        <br />
        <div class="row mb-5">
            <div class="col-12">
                <div class="row align-items-center justify-content-center">
                    <div class="col kpi-block">
                        <div class="kpi-number-container text-center d-flex">
                            <span class="kpi-number text-info">
                                <span v-if="medianTimeSeconds">
                                    {{ formatDurationFromSeconds(medianTimeSeconds) }}
                                </span>
                                <span v-else>-</span>
                            </span>
                        </div>
                        <p class="text-center medium mb-0">Median Time<br /></p>
                    </div>
                    <div class="col kpi-block">
                        <div class="kpi-number-container text-center d-flex">
                            <span class="kpi-number text-success">
                                <span v-if="fastestTimeSeconds" class="kpi-number">
                                    {{ formatDurationFromSeconds(fastestTimeSeconds) }}
                                </span>
                                <span v-else>-</span>
                            </span>
                        </div>
                        <p class="text-center medium mb-0">Fastest Time<br /></p>
                    </div>
                    <div class="col">
                        <div class="kpi-number-container text-center d-flex">
                            <span class="kpi-number text-danger">
                                <span style="font-size: 1.75rem" v-if="timer.time_limit_seconds">{{
                                    learnersExceeded
                                }}</span>
                                <span v-else>-</span>
                            </span>
                        </div>
                        <p class="text-center medium mb-0">Learners Exceeded the Time Limit</p>
                    </div>
                </div>
            </div>
        </div>
        <br />
        <br />
        <div class="row">
            <div class="col-12">
                <base-table
                    :table-data="sessionTimers"
                    :config="tableConfig"
                    empty-message="No learners have reached this timer in the provided time frame yet."
                    :rows-per-page="20"
                ></base-table>
            </div>
        </div>
    </div>
</template>

<script>
import BaseTable from "@/components/BaseTable"
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "TimerPerformanceReport",
    components: {
        BaseTable,
    },
    props: {
        timerId: {
            type: Number,
            required: false,
        },
        libraryItemId: {
            type: String,
            required: false,
            default: null,
        },
        startDate: {
            type: String,
            required: false,
            default: null,
        },
        endDate: {
            type: String,
            required: false,
            default: null,
        },
        launchRequestId: {
            type: Number,
            required: false,
            default: null,
        },
        includeShareLinks: {
            type: Boolean,
            required: false,
            default: true,
        },
        shareLinkId: {
            type: Number,
            required: false,
            default: null,
        },
        pageId: {
            type: Number,
            required: false,
            default: null,
        },
        // When true, links to the case editor view are hidden.
        disableEditorLinks: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isRendered: false,
            timer: null,
            sessionTimers: [],
            averageTimeSeconds: null,
            fastestTimeSeconds: null,
            medianTimeSeconds: null,
            learnersExceeded: null,
            learnerCount: null,
        }
    },
    computed: {
        tableConfig() {
            let vm = this
            return {
                initialSort: "Learner",
                initialSortDescend: false,
                multiSelect: false,
                actions: null,
                columns: [
                    {
                        name: "Learner",
                        sort: true,
                        display: (row) => {
                            return row.session.learner_identifier || "[Anonymous Learner]"
                        },
                        link: (row) => {
                            if (row.session.organization_member) {
                                return `/users/${row.session.organization_member.id}/`
                            }
                            return false
                        },
                        badges: (row) => {
                            if (row.session.learner_is_link_user) {
                                return ["Share Link Learner"]
                            }
                        },
                    },
                    {
                        name: "Elapsed Time",
                        sort: true,
                        key: "elapsed_seconds_stopped",
                        display(row) {
                            return Utilities.formatDurationFromSec(row.elapsed_seconds_stopped)
                        },
                    },
                    {
                        name: "Exceeded Limit",
                        sort: true,
                        key: "time_limit_exceeded",
                        display(row) {
                            if (row.time_limit_exceeded) {
                                return "Exceeded"
                            }
                            return "Under"
                        },
                        classes(row) {
                            let classList = ["font-weight-bold", "small"]
                            let specialClass = row.time_limit_exceeded ? "text-danger" : ""
                            classList.push(specialClass)
                            return classList.join(" ")
                        }
                    },
                    {
                        name: "Exceeded Time",
                        sort: true,
                        key: "exceeded_seconds",
                        display(row) {
                            return Utilities.formatDurationFromSec(row.exceeded_seconds)
                        },
                        hidden() {
                            return !vm.timer.time_limit_seconds
                        }
                    },
                    {
                        name: "Session Status",
                        sort: true,
                        key: "session.complete",
                        display(row) {
                            return row.session.complete ? "Complete" : "Incomplete"
                        },
                        classes(row) {
                            let classList = ["font-weight-bold", "small"]
                            let specialClass = row.session.complete ? "text-success" : ""
                            classList.push(specialClass)
                            return classList.join(" ")
                        }
                    },
                ],
            }
        },
        startNodeLink() {
            if (!this.timer || !this.timer.start_node) {
                return null
            }
            return `/library/${this.timer.case_id}/nodes/${this.timer.start_node.id}/`
        },
        endNodeLink() {
            if (!this.timer || !this.timer.end_node) {
                return null
            }
            return `/library/${this.timer.case_id}/nodes/${this.timer.end_node.id}/`
        },
    },
    methods: {
        refresh() {
            this.$store.dispatch("addLoad")
            let query = {
                libraryItemId: this.libraryItemId,
                timerId: this.timerId,
                startDate: this.startDate,
                endDate: this.endDate,
                launchRequestId: this.launchRequestId,
                includeShareLinks: this.includeShareLinks,
                shareLinkId: this.shareLinkId,
                pageId: this.pageId
            }

            let vm = this
            http.get("/api/reports/timer/", {
                params: query,
            })
                .then(function (response) {
                    vm.isRendered = true
                    vm.timer = response.data.timer
                    vm.sessionTimers = response.data.session_timers
                    vm.medianTimeSeconds = response.data.median_time_seconds
                    vm.fastestTimeSeconds = response.data.fastest_time_seconds
                    vm.learnersExceeded = response.data.learners_exceeded
                    vm.learnerCount = response.data.learner_count
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        formatDurationFromSeconds(seconds) {
            return Utilities.formatDurationFromSec(seconds)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.kpi-block {
    border-right: 1px solid $gray-300;
}

.kpi-number-container {
    height: 75px;
}

.kpi-number {
    margin: auto;
}

.kpi-number {
    font-weight: 400;
    font-size: 1.5rem;
}
</style>